import React, { useEffect, useRef, useState } from 'react';

import ModalFooter from 'CommonComponents/ModalFooter';

import { useToast } from 'Hooks/useToast';

const MAX_FILE_SIZE_MB = 10;
const PREVIEW_ROW_LIMIT = 3;

const badgeClass = (action) => {
    const base = 'tw-inline-flex tw-items-center tw-text-xs tw-font-medium tw-px-2.5 tw-py-0.5 tw-rounded-full tw-border';
    if (action === 'new') return `${base} tw-bg-background-5/10 tw-border-background-5/30 tw-text-background-5`;
    if (action === 'update') return `${base} tw-bg-blue-10 tw-border-blue-25 tw-text-blue`;
    return `${base} tw-bg-red-25 tw-border-red-50 tw-text-red`;
};

const badgeLabel = (action) => {
    if (action === 'new') return $t('New');
    if (action === 'update') return $t('Update');
    return $t('Error');
};

/**
 * Bulk upload of departments and centres: template download, upload, preview
 * with row-level errors, then confirm. Confirm imports every row or none, so a
 * 422 carries the rows back with their errors and replaces the preview.
 */
export default function BudgetStructureBulkUpload({ bulkPreviewUrl, bulkConfirmUrl, templateUrl, onSuccess, onDirtyChange, onCancel, projectLabel }) {
    const toast = useToast();
    const fileInputRef = useRef(null);

    const [file, setFile] = useState(null);
    const [preview, setPreview] = useState(null); // null = not yet parsed
    const [isPreviewing, setIsPreviewing] = useState(false);
    const [isConfirming, setIsConfirming] = useState(false);
    const [showAll, setShowAll] = useState(false);

    // A staged-but-unconfirmed file counts as unsaved data for the tab-switch guard.
    useEffect(() => {
        onDirtyChange?.(!!file);
    }, [file, onDirtyChange]);

    const hasErrors = preview?.rows?.some((r) => r.action === 'error');
    const errorCount = preview?.rows?.filter((r) => r.action === 'error').length ?? 0;
    const displayRows = showAll ? preview?.rows : preview?.rows?.slice(0, PREVIEW_ROW_LIMIT);

    const handleFileChange = (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        if (f.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
            toast.error($t('File exceeds {{mb}}MB limit.', { mb: MAX_FILE_SIZE_MB }));
            return;
        }
        setFile(f);
        setPreview(null);
        setShowAll(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        const f = e.dataTransfer.files?.[0];
        if (f) {
            if (f.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
                toast.error($t('File exceeds {{mb}}MB limit.', { mb: MAX_FILE_SIZE_MB }));
                return;
            }
            setFile(f);
            setPreview(null);
            setShowAll(false);
        }
    };

    // Auto-preview when a file is selected
    useEffect(() => {
        if (!file) return;
        setIsPreviewing(true);

        const formData = new FormData();
        formData.append('import_excel', file);

        axios
            .post(bulkPreviewUrl, formData, { headers: { 'Content-Type': 'multipart/form-data' } })
            .then((res) => setPreview(res.data))
            .catch(() => toast.error($t('Could not parse the file.')))
            .finally(() => setIsPreviewing(false));
    }, [file]);

    const handleConfirm = () => {
        if (!file || hasErrors) return;
        setIsConfirming(true);

        const formData = new FormData();
        formData.append('import_excel', file);

        axios
            .post(bulkConfirmUrl, formData, { headers: { 'Content-Type': 'multipart/form-data' } })
            .then(() => {
                toast.success($t('Import completed successfully.'));
                setFile(null);
                setPreview(null);
                onSuccess && onSuccess();
            })
            .catch((e) => {
                const rows = e?.response?.status === 422 ? e.response.data?.rows : null;
                if (rows) {
                    setPreview((current) => ({ ...current, rows }));
                    setShowAll(true);
                }

                const msg = e?.response?.data?.message ?? $t('Import failed.');
                toast.error(msg);
            })
            .finally(() => setIsConfirming(false));
    };

    return (
        <div className="tw-flex tw-flex-col tw-gap-4">
            {/* Info banner */}
            <div className="tw-bg-blue-10 tw-border tw-border-blue-25 tw-rounded tw-p-3 tw-text-sm tw-text-blue">
                {$t('Template supports new entries, additions to existing departments, and updates to text fields on existing items.')}
            </div>

            {templateUrl && (
                <div>
                    <a href={templateUrl} className="tw-inline-flex tw-items-center tw-gap-1.5 tw-text-sm tw-font-medium tw-text-blue hover:tw-underline">
                        {$t('Download template')}
                    </a>
                </div>
            )}

            {!file && (
                <div
                    className="tw-border-2 tw-border-dashed tw-border-charcoal-25 tw-rounded tw-p-8 tw-text-center tw-cursor-pointer hover:tw-border-blue-50 tw-transition-colors"
                    onDrop={handleDrop}
                    onDragOver={(e) => e.preventDefault()}
                    onClick={() => fileInputRef.current?.click()}
                >
                    <p className="tw-text-gray-500 tw-text-sm tw-mb-2">{$t('Drop your .xlsx file here, or click to browse')}</p>
                    <p className="tw-text-gray-400 tw-text-xs">{$t('Maximum {{mb}}MB', { mb: MAX_FILE_SIZE_MB })}</p>
                    <input ref={fileInputRef} type="file" accept=".xlsx,.xls" className="tw-hidden" onChange={handleFileChange} />
                </div>
            )}

            {file && !preview && isPreviewing && <div className="tw-text-sm tw-text-gray-500">{$t('Parsing file…')}</div>}

            {file && preview && (
                <div className="tw-flex tw-flex-col tw-gap-3">
                    {/* Uploaded file — boxed */}
                    <div className="tw-flex tw-items-center tw-justify-between tw-rounded-lg tw-border tw-border-charcoal-15 tw-bg-white tw-px-4 tw-py-3">
                        <div className="tw-flex tw-flex-col">
                            <span className="tw-text-sm tw-font-medium tw-text-charcoal">{file.name}</span>
                            <span className="tw-text-xs tw-text-charcoal-50">
                                {$t('Parsed {{n}} rows · {{e}} errors', {
                                    n: preview.rows.length,
                                    e: errorCount,
                                })}
                            </span>
                        </div>
                        <button
                            type="button"
                            className="tw-text-xs tw-text-red hover:tw-text-red-75"
                            onClick={() => {
                                setFile(null);
                                setPreview(null);
                            }}
                        >
                            {$t('Remove')}
                        </button>
                    </div>

                    {/* Counters — coloured summary cards */}
                    <div className="tw-grid tw-grid-cols-3 tw-gap-4">
                        <div className="tw-rounded-lg tw-border tw-border-background-5/30 tw-bg-background-5/10 tw-py-4 tw-text-center">
                            <div className="tw-text-3xl tw-font-bold tw-text-background-5">{preview.counts.new_departments}</div>
                            <div className="tw-mt-1 tw-text-xs tw-font-medium tw-uppercase tw-tracking-wide tw-text-background-5">{$t('New Depts')}</div>
                        </div>
                        <div className="tw-rounded-lg tw-border tw-border-background-5/30 tw-bg-background-5/10 tw-py-4 tw-text-center">
                            <div className="tw-text-3xl tw-font-bold tw-text-background-5">{preview.counts.new_costcenters}</div>
                            <div className="tw-mt-1 tw-text-xs tw-font-medium tw-uppercase tw-tracking-wide tw-text-background-5">{$t('New Cost Centres')}</div>
                        </div>
                        <div className="tw-rounded-lg tw-border tw-border-blue-25 tw-bg-blue-10 tw-py-4 tw-text-center">
                            <div className="tw-text-3xl tw-font-bold tw-text-blue">{preview.counts.updates}</div>
                            <div className="tw-mt-1 tw-text-xs tw-font-medium tw-uppercase tw-tracking-wide tw-text-blue">{$t('Updates')}</div>
                        </div>
                    </div>

                    {/* Preview table */}
                    <div>
                        <div className="tw-mb-1 tw-text-sm tw-text-charcoal-50">{$t('Preview')}</div>
                        <div className="tw-overflow-x-auto tw-rounded-lg tw-border tw-border-charcoal-15">
                            <table className="tw-w-full tw-text-sm">
                                <thead>
                                    <tr className="tw-border-b tw-border-charcoal-15 tw-bg-background tw-text-charcoal-50">
                                        <th className="tw-px-4 tw-py-2 tw-text-start tw-font-medium">{$t('Item')}</th>
                                        <th className="tw-px-4 tw-py-2 tw-text-start tw-font-medium">{$t('Parent')}</th>
                                        <th className="tw-px-4 tw-py-2 tw-text-start tw-font-medium">{$t('Action')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {displayRows.map((row, i) => (
                                        <tr key={i} className="tw-border-b tw-border-charcoal-15 last:tw-border-0">
                                            <td className="tw-px-4 tw-py-2 tw-text-charcoal">{row.is_costcenter ? row.costcenter : row.department}</td>
                                            <td className="tw-px-4 tw-py-2 tw-text-charcoal-50">{row.is_costcenter ? row.department : '—'}</td>
                                            <td className="tw-px-4 tw-py-2">
                                                <span className={badgeClass(row.action)}>{badgeLabel(row.action)}</span>
                                                {row.error && <span className="tw-ms-2 tw-text-xs tw-text-red">{row.error}</span>}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        {preview.rows.length > PREVIEW_ROW_LIMIT && !showAll && (
                            <button type="button" className="tw-text-xs tw-text-blue hover:tw-underline tw-mt-2" onClick={() => setShowAll(true)}>
                                {$t('View all {{n}} rows', { n: preview.rows.length })}
                            </button>
                        )}
                    </div>

                    {hasErrors && (
                        <div className="tw-bg-red-25 tw-border tw-border-red-50 tw-rounded tw-p-3 tw-text-sm tw-text-red">
                            {$t('{{n}} row(s) have errors. Fix them in the file and re-upload before confirming.', { n: errorCount })}
                        </div>
                    )}
                </div>
            )}

            <div className="tw-mt-6 tw-pt-4 tw-border-t tw-border-gray-200">
                <ModalFooter
                    startContent={projectLabel && <span className="tw-text-sm tw-text-charcoal-50 tw-whitespace-nowrap">{projectLabel}</span>}
                    cancelButtonText={$t('Cancel')}
                    primaryButtonText={$t('Confirm')}
                    primaryButtonDisabled={!preview || hasErrors || isConfirming}
                    onCancel={onCancel}
                    handleSubmit={handleConfirm}
                    isSubmitting={isConfirming}
                    submitButtonVariant="primary"
                />
            </div>
        </div>
    );
}
